import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import type { BotPersonaView } from '../api/types.js';
import { Badge, Card } from './ui.js';
import { Button } from './ui/Button.js';
import { Input } from './ui/Input.js';

const PERSONA_TEXT_MIN = 20;

export function PersonaWizard({ persona }: { persona?: BotPersonaView }) {
  const queryClient = useQueryClient();
  const [personaName, setPersonaName] = useState('');
  const [personaText, setPersonaText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!persona) return;
    setPersonaName(persona.personaName ?? '');
    // API always returns the Loomy-facing string (stored freeform or flattened).
    setPersonaText(persona.personaText ?? '');
  }, [persona]);

  const configured = persona && !persona.needsConfiguration;

  async function draftFromMyData() {
    setBusy(true);
    try {
      const { draft, personaText: draftedText, connected } = await api.post<{
        draft: {
          biographyFacts: string[];
          styleBullets?: string[];
          ownerBlurb?: string;
          voiceExcerpt: string;
          sources: {
            kanbanCards: number;
            ndocPages: number;
            ownPosts: number;
            memorySnippets?: number;
            llm: boolean;
            llmVia?: string;
          };
        };
        personaText: string;
        connected: { kanban: boolean; ndoc: boolean; mattermost: boolean; memory?: boolean };
      }>('/api/user/personal-bot/persona/from-my-data', {});

      if (draftedText?.trim()) setPersonaText(draftedText);
      if (!personaName.trim() && draft.ownerBlurb) {
        /* keep existing name */
      }

      const { kanbanCards, ndocPages, ownPosts, memorySnippets = 0, llm, llmVia } = draft.sources;
      if (!kanbanCards && !ndocPages && !ownPosts && !memorySnippets) {
        const off = (['kanban', 'ndoc', 'mattermost', 'memory'] as const).filter(
          (k) => !connected[k],
        );
        toast.error(
          off.length
            ? `Nothing found. Not connected: ${off.join(', ')}`
            : 'Nothing found that belongs to you in Kanban, NDoc, Mattermost, or Memory',
        );
        return;
      }
      toast.success(
        `Read ${kanbanCards} cards, ${ndocPages} pages, ${ownPosts} messages, ${memorySnippets} memories` +
          (llm ? ` · drafted by ${llmVia ?? 'LLM'}` : ' · LLM unavailable, used fallbacks') +
          ' — review the text and save',
      );
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function savePersona() {
    const text = personaText.trim();
    if (!personaName.trim()) {
      toast.error('Persona name is required');
      return;
    }
    if (text.length < PERSONA_TEXT_MIN) {
      toast.error(`Persona text needs at least ${PERSONA_TEXT_MIN} characters`);
      return;
    }
    setBusy(true);
    try {
      await api.put('/api/user/personal-bot/persona', {
        personaName: personaName.trim(),
        personaText: text,
      });
      toast.success('Persona saved and pushed to the bot');
      void queryClient.invalidateQueries({ queryKey: ['personal-bot'] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-4 border-t border-[var(--color-line)] p-4 pt-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-ink-faint)]">
          Bot persona
        </div>
        {configured ? (
          <Badge tone="good">{persona?.personaName}</Badge>
        ) : (
          <Badge tone="warn">Not configured</Badge>
        )}
      </div>
      <p className="mb-3 text-xs leading-relaxed text-[var(--color-ink-muted)]">
        One block of text is what the bot actually uses. Edit anytime, then{' '}
        <strong>Save &amp; push to bot</strong>. Human name (e.g. Jeff) is not the Mattermost @handle.
      </p>
      <div className="mb-3 max-w-lg">
        <Input
          placeholder="Persona name (e.g. Jeff)"
          value={personaName}
          maxLength={32}
          onChange={(e) => setPersonaName(e.target.value)}
        />
      </div>
      <textarea
        className="mb-3 w-full rounded-md border border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] leading-relaxed"
        rows={16}
        value={personaText}
        onChange={(e) => setPersonaText(e.target.value)}
        placeholder="How this bot should sound and who they are…"
      />
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => void draftFromMyData()}>
          Re-draft from my work data
        </Button>
        <Button size="sm" disabled={busy} onClick={() => void savePersona()}>
          Save &amp; push to bot
        </Button>
      </div>
    </Card>
  );
}
